// Utility functions for Habit Tracker
// XP, Levels, Streaks, Completion Rates, Stability Score

// --- DATE HELPERS ---

export function getLocalDateStr(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return getLocalDateStr(d);
}

export function getWeekNumber(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const dayOfYear = Math.floor((d - new Date(d.getFullYear(), 0, 1)) / 86400000);
  return Math.ceil((dayOfYear + new Date(d.getFullYear(), 0, 1).getDay() + 1) / 7);
}

export function getDayOfWeek(dateStr) {
  return new Date(dateStr + 'T12:00:00').getDay(); // 0=Sun, 1=Mon...6=Sat
}

export function getDatesBetween(startStr, endStr) {
  const dates = [];
  let current = new Date(startStr + 'T12:00:00');
  const end = new Date(endStr + 'T12:00:00');
  while (current <= end) {
    dates.push(getLocalDateStr(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

// --- SCHEDULE HELPERS ---

export function isHabitDueOnDate(habit, dateStr) {
  if (habit.status === 'paused' || habit.status === 'archived') return false;
  if (dateStr < habit.startDate) return false;
  if (habit.skippedDates?.includes(dateStr)) return false;

  const freq = habit.schedule?.frequencyType || 'daily';
  if (freq === 'daily') return true;

  if (freq === 'custom' && habit.schedule?.daysOfWeek?.length > 0) {
    const dow = getDayOfWeek(dateStr);
    return habit.schedule.daysOfWeek.includes(dow);
  }

  // weekly: always "due" but tracked by weekly target
  if (freq === 'weekly') return true;

  // schichten: determine shift type for this date and check if a time is set
  if (freq === 'schichten') {
    const rhythmus = habit.schedule?.schichtRhythmus;
    const zeiten   = habit.schedule?.schichtZeiten;
    if (!rhythmus?.days?.length || !rhythmus.cycleLength || !zeiten) return false;

    const startMs  = new Date(rhythmus.startDate + 'T12:00:00').getTime();
    const targetMs = new Date(dateStr + 'T12:00:00').getTime();
    const offset   = Math.round((targetMs - startMs) / 86400000);
    if (offset < 0) return false;

    const pos     = ((offset % rhythmus.cycleLength) + rhythmus.cycleLength) % rhythmus.cycleLength;
    const schicht = rhythmus.days[pos] ?? 'Frei'; // null → Frei

    const z = zeiten[schicht];
    if (!z) return false;
    // Backward compat: old format was string (time), new format is object
    if (typeof z === 'string') return !!z;
    if (!z.enabled) return false;
    if (z.type === 'alle') return true;
    if (z.type === 'bestimmte') return (z.enabledCyclePositions || []).includes(pos);
    return false;
  }

  return true;
}

/** Returns the schicht type for a habit on a given date, or null if not applicable. */
export function getSchichtForDate(habit, dateStr) {
  if (habit.schedule?.frequencyType !== 'schichten') return null;
  const rhythmus = habit.schedule?.schichtRhythmus;
  if (!rhythmus?.days?.length) return null;
  const startMs  = new Date(rhythmus.startDate + 'T12:00:00').getTime();
  const targetMs = new Date(dateStr + 'T12:00:00').getTime();
  const offset   = Math.round((targetMs - startMs) / 86400000);
  if (offset < 0) return null;
  const pos = ((offset % rhythmus.cycleLength) + rhythmus.cycleLength) % rhythmus.cycleLength;
  return rhythmus.days[pos] ?? null;
}

export function getWeeklyProgress(habit, logs, weekStartStr, weekEndStr) {
  const target = habit.schedule?.weeklyTargetCount || 7;
  const weekLogs = logs.filter(l => l.date >= weekStartStr && l.date <= weekEndStr && l.done);
  return { done: weekLogs.length, target };
}

// --- XP & LEVELS ---

const XP_PER_COMPLETION = 10;

const LEVEL_NAMES = [
  'Anfänger',      // 1
  'Starter',        // 2
  'Dranbleiber',    // 3
  'Stabil',         // 4
  'Fokussiert',     // 5
  'Diszipliniert',  // 6
  'Konsequent',     // 7
  'Unaufhaltsam',   // 8
  'Meister',        // 9
  'Architekt',      // 10
  'Stratege',       // 11
  'Legende'         // 12
];

export function calculateTotalXP(allLogs) {
  return allLogs.filter(l => l.done).length * XP_PER_COMPLETION;
}

export function calculateLevel(totalXP) {
  return Math.floor(totalXP / 200) + 1;
}

export function getLevelName(level) {
  if (level <= 0) return LEVEL_NAMES[0];
  if (level > LEVEL_NAMES.length) return LEVEL_NAMES[LEVEL_NAMES.length - 1];
  return LEVEL_NAMES[level - 1];
}

export function xpForNextLevel(totalXP) {
  const level = calculateLevel(totalXP);
  const xpNeeded = level * 200;
  return { current: totalXP, needed: xpNeeded, progress: (totalXP % 200) / 200 };
}

// --- STREAKS ---

export function calculateStreak(habit, logs) {
  const doneDates = new Set(logs.filter(l => l.done).map(l => l.date));
  const today = getLocalDateStr();
  let streak = 0;
  let date = today;

  // If not done today, start from yesterday
  if (!doneDates.has(date)) {
    date = addDays(date, -1);
    if (!doneDates.has(date)) return 0;
  }

  while (doneDates.has(date)) {
    // Only count if habit was due on this date
    if (isHabitDueOnDate(habit, date)) {
      streak++;
    }
    date = addDays(date, -1);
    if (date < habit.startDate) break;
  }
  return streak;
}

export function calculateBestStreak(habit, logs) {
  const doneDates = logs.filter(l => l.done).map(l => l.date).sort();
  if (doneDates.length === 0) return 0;

  let best = 1, current = 1;
  for (let i = 1; i < doneDates.length; i++) {
    if (addDays(doneDates[i - 1], 1) === doneDates[i]) {
      current++;
      best = Math.max(best, current);
    } else {
      current = 1;
    }
  }
  return best;
}

// --- COMPLETION RATES ---

export function completionRate(habit, logs, days) {
  const today = getLocalDateStr();
  const startDate = addDays(today, -(days - 1));
  const effectiveStart = habit.startDate > startDate ? habit.startDate : startDate;
  const dates = getDatesBetween(effectiveStart, today);

  const dueDates = dates.filter(d => isHabitDueOnDate(habit, d));
  if (dueDates.length === 0) return 0;

  const doneDates = new Set(logs.filter(l => l.done).map(l => l.date));
  const completed = dueDates.filter(d => doneDates.has(d)).length;
  return Math.round((completed / dueDates.length) * 100);
}

// --- STABILITY SCORE ---

export function calculateStability(habit, logs) {
  const rate14 = completionRate(habit, logs, 14);

  // Resilience: average days to resume after a miss
  const doneDates = logs.filter(l => l.done).map(l => l.date).sort();
  let gaps = [];
  for (let i = 1; i < doneDates.length; i++) {
    const diff = Math.round((new Date(doneDates[i] + 'T12:00:00') - new Date(doneDates[i - 1] + 'T12:00:00')) / 86400000);
    if (diff > 1) gaps.push(diff - 1);
  }
  const avgResumeDays = gaps.length > 0 ? gaps.reduce((a, b) => a + b, 0) / gaps.length : 0;

  const resilience = Math.max(0, Math.min(100, 100 - avgResumeDays * 20));
  const stability = Math.round(0.7 * rate14 + 0.3 * resilience);

  const daysSinceStart = Math.round(
    (new Date(getLocalDateStr() + 'T12:00:00') - new Date(habit.startDate + 'T12:00:00')) / 86400000
  );

  return {
    score: stability,
    rate14,
    resilience: Math.round(resilience),
    canPromote: stability >= 70 && daysSinceStart >= 14
  };
}

// --- NEW HABIT LIMIT ---

export function countNewHabits(habits) {
  return habits.filter(h => h.status === 'new').length;
}

export function canCreateNewHabit(habits) {
  return countNewHabits(habits) < 3;
}

// --- FORMAT DURATION (for display on HabitCard / HabitDetail) ---

export function formatDuration(startDate) {
  const days = Math.round(
    (new Date(getLocalDateStr() + 'T12:00:00') - new Date(startDate + 'T12:00:00')) / 86400000
  );
  const d = Math.max(1, days); // minimum 1 Tag (heute = Tag 1)
  if (d < 7) return `${d} Tag${d === 1 ? '' : 'e'}`;
  if (d < 60) return `${Math.floor(d / 7)} Wo.`;
  if (d < 365) return `${Math.floor(d / 30)} Mon.`;
  const years = Math.floor(d / 365);
  const remMonths = Math.floor((d % 365) / 30);
  return remMonths > 0 ? `${years} J. ${remMonths} Mon.` : `${years} Jahr${years > 1 ? 'e' : ''}`;
}



export function shouldSuggestProgression(logs) {
  const recent = logs.filter(l => l.done).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);
  if (recent.length < 5) return false;
  const easyCount = recent.filter(l => l.rating === 'easy').length;
  return easyCount >= 3;
}

// --- AUTO MILESTONES ---
// Returns the highest-priority automatic milestone to show, or null
// Priority: habit-formed > habit-anniversary > streak-milestones
export function checkAutoMilestone(habit, logs) {
  const today = getLocalDateStr();
  const doneDates = new Set(logs.filter(l => l.done).map(l => l.date));

  const daysSinceStart = Math.round(
    (new Date(today + 'T12:00:00') - new Date(habit.startDate + 'T12:00:00')) / 86400000
  );

  // 1. Habit-formed milestone (90+ days AND 75%+ completion rate, fires once)
  if (!habit.bigMilestoneAchieved) {
    if (daysSinceStart >= 90) {
      const rate = completionRate(habit, logs, 90);
      if (rate >= 75) {
        return { type: 'habit-formed', daysSinceStart };
      }
    }
  }

  // 2. Extended milestones after Option B (180/365 days + 75% rate)
  if (habit.bigMilestoneAchieved && habit.bigMilestoneExtended) {
    if (daysSinceStart >= 365 && !habit.milestone365Achieved) {
      const rate = completionRate(habit, logs, Math.min(365, daysSinceStart));
      if (rate >= 75) return { type: 'habit-anniversary', days: 365 };
    }
    if (daysSinceStart >= 180 && !habit.milestone180Achieved) {
      const rate = completionRate(habit, logs, Math.min(180, daysSinceStart));
      if (rate >= 75) return { type: 'habit-anniversary', days: 180 };
    }
  }

  // 3. Streak milestones (7 / 30 / 60 days) — only when today is done
  if (doneDates.has(today)) {
    let streak = 0;
    let checkDate = today;
    while (doneDates.has(checkDate)) {
      streak++;
      checkDate = addDays(checkDate, -1);
      if (checkDate < habit.startDate) break;
    }
    if ([7, 30, 60].includes(streak)) {
      return { type: `streak-${streak}`, streak };
    }
  }

  return null;
}
